import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/BillingProvidersService';
import { useEffect, useState } from 'react';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications.tsx';

// Module-scoped global filter state (shared across components)
let globalFilters: Record<string, string> = { status: 'Enabled' };
const filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useBillingProvidersFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return { filters, handleSetFilters, handleClearFilters };
};

export const useBillingProviders = () => {
    const queryClient = useQueryClient();
    const { filters } = useBillingProvidersFilter();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});

    const sort = { column: 'created', direction: 'desc' as const };

    const billingProvidersQuery = useInfiniteQuery({
        queryKey: ['billing-providers', keyword, filters, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            fetchRecords({ limit, sort, nextToken: pageParam, filter: filters }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Billing Provider Deleted');
            queryClient.invalidateQueries({ queryKey: ['billing-providers'] });
        },
        onError: notifyError
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({ queryKey: ['billing-providers'] });
    };

    const fetchMoreRecords = () => {
        if (billingProvidersQuery.hasNextPage) {
            billingProvidersQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = billingProvidersQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: billingProvidersQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: billingProvidersQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        fetchMoreRecords,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};
